import { accountApi } from "@/modules/account/services";
import {
  accountClosureApplicationApi,
  accountOpeningApplicationApi,
  applicationApi,
  cardServiceInstructionApplicationApi,
  depositTerminationApplicationApi,
  loanTrancheDisbursementApplicationApi,
  payrollCardOpeningApplicationApi,
  periodicalPaymentsApplicationApi,
  phoneBankingApplicationApi,
  userOpeningApplicationApi,
} from "@/modules/application/services";
import { applicationReducer } from "@/modules/application/state";
import { authApi } from "@/modules/auth/services";
import { authReducer } from "@/modules/auth/state";
import { beneficiaryApi } from "@/modules/beneficiary/services";
import { bondApi } from "@/modules/bond/services";
import { cardApi } from "@/modules/card/services";
import { dashboardReducer } from "@/modules/dashboard/state";
import { depositApi } from "@/modules/deposit/services";
import { exchangeRateApi } from "@/modules/exchangeRate/services";
import { loanApi } from "@/modules/loan/services";
import { stockApi } from "@/modules/stock/services";
import {
  accountTransferTemplateApi,
  budgetTransferTemplateApi,
  cardTransferTemplateApi,
  currencyExchangeTemplateApi,
  loanRepaymentTemplateApi,
  ownTransferTemplateApi,
  templateApi,
} from "@/modules/template/services";
import {
  accountTransferApi,
  budgetTransferApi,
  cardTransferApi,
  currencyExchangeApi,
  groupTransferApi,
  loanRepaymentApi,
  transactionApi,
} from "@/modules/transaction/services";
import { transactionReducer } from "@/modules/transaction/state";
import { commonApi } from "@/shared/services";
import { commonReducer } from "@/shared/state/common";
import { featureToggleReducer } from "@/shared/state/featureToggle";
import { languageReducer } from "@/shared/state/language";
import { notificationReducer } from "@/shared/state/notification";
import { staticUrlsReducer } from "@/shared/state/staticUrls";
import { themeReducer } from "@/shared/state/theme";
import { configureStore } from "@reduxjs/toolkit";

export const combinedReducers = {
  [authApi.reducerPath]: authApi.reducer,
  [commonApi.reducerPath]: commonApi.reducer,
  [accountApi.reducerPath]: accountApi.reducer,
  [beneficiaryApi.reducerPath]: beneficiaryApi.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [cardApi.reducerPath]: cardApi.reducer,
  [budgetTransferApi.reducerPath]: budgetTransferApi.reducer,
  [exchangeRateApi.reducerPath]: exchangeRateApi.reducer,
  [stockApi.reducerPath]: stockApi.reducer,
  [transactionApi.reducerPath]: transactionApi.reducer,
  [loanApi.reducerPath]: loanApi.reducer,
  [bondApi.reducerPath]: bondApi.reducer,
  [depositApi.reducerPath]: depositApi.reducer,
  [templateApi.reducerPath]: templateApi.reducer,
  [accountTransferTemplateApi.reducerPath]: accountTransferTemplateApi.reducer,
  [accountTransferApi.reducerPath]: accountTransferApi.reducer,
  [cardTransferApi.reducerPath]: cardTransferApi.reducer,
  [cardTransferTemplateApi.reducerPath]: cardTransferTemplateApi.reducer,
  [ownTransferTemplateApi.reducerPath]: ownTransferTemplateApi.reducer,
  [groupTransferApi.reducerPath]: groupTransferApi.reducer,
  [currencyExchangeApi.reducerPath]: currencyExchangeApi.reducer,
  [currencyExchangeTemplateApi.reducerPath]: currencyExchangeTemplateApi.reducer,
  [loanRepaymentApi.reducerPath]: loanRepaymentApi.reducer,
  [loanRepaymentTemplateApi.reducerPath]: loanRepaymentTemplateApi.reducer,
  [budgetTransferTemplateApi.reducerPath]: budgetTransferTemplateApi.reducer,
  [accountOpeningApplicationApi.reducerPath]: accountOpeningApplicationApi.reducer,
  [accountClosureApplicationApi.reducerPath]: accountClosureApplicationApi.reducer,
  [depositTerminationApplicationApi.reducerPath]: depositTerminationApplicationApi.reducer,
  [userOpeningApplicationApi.reducerPath]: userOpeningApplicationApi.reducer,
  [phoneBankingApplicationApi.reducerPath]: phoneBankingApplicationApi.reducer,
  [payrollCardOpeningApplicationApi.reducerPath]: payrollCardOpeningApplicationApi.reducer,
  [periodicalPaymentsApplicationApi.reducerPath]: periodicalPaymentsApplicationApi.reducer,
  [cardServiceInstructionApplicationApi.reducerPath]: cardServiceInstructionApplicationApi.reducer,
  [loanTrancheDisbursementApplicationApi.reducerPath]: loanTrancheDisbursementApplicationApi.reducer,
  transactions: transactionReducer,
  applications: applicationReducer,
  common: commonReducer,
  theme: themeReducer,
  auth: authReducer,
  dashboard: dashboardReducer,
  notification: notificationReducer,
  language: languageReducer,
  staticUrls: staticUrlsReducer,
  featureToggle: featureToggleReducer,
};

export const store = configureStore({
  reducer: combinedReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,
      commonApi.middleware,
      accountApi.middleware,
      beneficiaryApi.middleware,
      applicationApi.middleware,
      cardApi.middleware,
      budgetTransferApi.middleware,
      exchangeRateApi.middleware,
      stockApi.middleware,
      transactionApi.middleware,
      loanApi.middleware,
      bondApi.middleware,
      depositApi.middleware,
      templateApi.middleware,
      accountTransferTemplateApi.middleware,
      ownTransferTemplateApi.middleware,
      accountTransferApi.middleware,
      cardTransferApi.middleware,
      cardTransferTemplateApi.middleware,
      groupTransferApi.middleware,
      currencyExchangeApi.middleware,
      currencyExchangeTemplateApi.middleware,
      loanRepaymentApi.middleware,
      loanRepaymentTemplateApi.middleware,
      budgetTransferTemplateApi.middleware,
      accountOpeningApplicationApi.middleware,
      accountClosureApplicationApi.middleware,
      cardServiceInstructionApplicationApi.middleware,
      userOpeningApplicationApi.middleware,
      phoneBankingApplicationApi.middleware,
      periodicalPaymentsApplicationApi.middleware,
      loanTrancheDisbursementApplicationApi.middleware,
      depositTerminationApplicationApi.middleware,
      payrollCardOpeningApplicationApi.middleware,
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
